import posthog, { type CaptureResult } from 'posthog-js'
import {
  getObservabilitySessionId,
  OBSERVABILITY_SESSION_ID_KEY,
} from '~/services/observability/correlation'
import { getApiEnvironment, isElectron } from '~/utils/common'

import { FeatureFlag } from '@mangovoice/constants'
import {
  getPosthogConfig,
  isPosthogReady,
  isPosthogSkipped,
  markPosthogReady,
  markPosthogSkipped,
} from '@mangovoice/core'
import { Pbx, SessionUser } from '@mangovoice/types'

declare global {
  interface Window {
    posthog?: typeof posthog
  }
}

// In localStorage (not just memory) so a reload or second tab — where PostHog
// restores the same distinct_id — doesn't re-fire identify()'s redundant $set.
const IDENTIFY_DEDUP_KEY = 'mango_ph_identify_state'

// Request-scoped flag property so `environment=staging` targeting cannot leak
// onto a production session of the same user (identify() would persist it).
let lastFlagEnvironment: string | undefined

// Build-capability version for the release-notifications UI (CUS-891). Bump on a
// contract change older builds get wrong; the flag gates on release_ui_contract
// >= N so those builds hide the toggle. Flag-eval only (never persisted): a
// persisted value would leak onto an older build via last-write-wins.
const RELEASE_UI_CONTRACT = 2

interface IdentifyDedupState {
  userId: string
  payloadKey: string
}

const readIdentifyDedupState = (): IdentifyDedupState | null => {
  try {
    const raw = localStorage.getItem(IDENTIFY_DEDUP_KEY)
    return raw ? (JSON.parse(raw) as IdentifyDedupState) : null
  } catch {
    return null
  }
}

const writeIdentifyDedupState = (state: IdentifyDedupState): void => {
  try {
    localStorage.setItem(IDENTIFY_DEDUP_KEY, JSON.stringify(state))
  } catch {
    // Storage unavailable: dedup degrades to per-load, still correct.
  }
}

const clearIdentifyDedupState = (): void => {
  try {
    localStorage.removeItem(IDENTIFY_DEDUP_KEY)
  } catch {
    // No-op when storage is unavailable.
  }
}

const isCaptureKilled = () =>
  posthog.isFeatureEnabled(FeatureFlag.PosthogCaptureEnabled, {
    send_event: false,
  }) !== true

/**
 * Single capture gate: every event type flows through before_send, so this drops
 * them all while PosthogCaptureEnabled is off. Fails safe — reads as not-true
 * until flags load, keeping capture off until it is explicitly enabled.
 */
export const applyCaptureKillSwitch = (
  event: CaptureResult | null,
): CaptureResult | null => (isCaptureKilled() ? null : event)

// The caller (UserState) passes these when a channel/version could not be
// resolved. Exported so the source and the guard below share one literal —
// changing the wording in one place must not silently break the placeholder
// match and re-persist an Unknown value.
export const UNKNOWN_CHANNEL = 'Unknown Channel Name'
export const UNKNOWN_APP_VERSION = 'Unknown App Version'

// Placeholders are treated as "no value" so they are cleared from the shared
// store rather than persisted.
const SUPER_PROPERTY_PLACEHOLDERS = new Set([
  UNKNOWN_CHANNEL,
  UNKNOWN_APP_VERSION,
])

const registerOrClearSuperProperty = (key: string, value?: string) => {
  if (value && !SUPER_PROPERTY_PLACEHOLDERS.has(value)) {
    posthog.register({ [key]: value })
  } else {
    posthog.unregister(key)
  }
}

// Async: main owns app.runningUnderARM64Translation. With arch and
// os_platform it separates Intel Macs from Apple silicon running an x64 build.
const registerArm64Translation = () => {
  const invoke = window.electron?.invoke
  if (!invoke) {
    posthog.unregister('arm64_translation')
    return
  }
  invoke('app:is-arm64-translation')
    .then((translated: unknown) => {
      if (typeof translated === 'boolean') {
        posthog.register({ arm64_translation: translated })
      } else {
        posthog.unregister('arm64_translation')
      }
    })
    .catch(() => {
      // Binaries without the handler can't report it.
      posthog.unregister('arm64_translation')
    })
}

export const initializePosthog = (
  pbx: Pbx,
  user?: SessionUser,
  channel?: string,
  appVersion?: string,
  partner?: string,
  buildChannel?: string,
  releaseLine?: string,
) => {
  // sessionUser is rehydrated from localStorage and can be a partial object
  // missing id; defer init until a real id lands (the caller re-runs).
  if (user?.id == null) {
    return
  }

  // Already resolved as "no PostHog for this region" — don't retry init.
  if (isPosthogSkipped()) {
    return
  }

  const config = getPosthogConfig(getApiEnvironment(), {
    us: import.meta.env.VITE_POSTHOG_KEY_US,
    uk: import.meta.env.VITE_POSTHOG_KEY_UK,
  })
  if (!config) {
    markPosthogSkipped()
    return
  }

  if (!isPosthogReady()) {
    posthog.init(config.key, {
      api_host: config.host,
      // Flag requests route through our ad-blocker-proof reverse proxy where
      // one exists for this environment; capture stays direct to PostHog.
      flags_api_host: config.flagsHost,
      // No anonymous profiles — controls MTU/cost.
      person_profiles: 'identified_only',
      // Off: nothing consumes it and it's high-volume/low-signal.
      autocapture: false,
      capture_pageview: true,
      // network_timing off to avoid capturing request URLs/timing (PHI surface).
      capture_performance: {
        web_vitals: true,
        network_timing: false,
        web_vitals_allowed_metrics: ['LCP', 'CLS', 'FCP', 'INP'],
      },
      session_recording: {
        // PHI-safe default: mask every input and all text.
        maskAllInputs: true,
        maskTextSelector: '*',
        // No local sampleRate — it would override the remotely managed rate.
      },
      before_send: applyCaptureKillSwitch,
    })
    window.posthog = posthog
    markPosthogReady()
  }

  // Super properties (attached to every event). platform distinguishes desktop,
  // which otherwise reports as Chrome in the auto-captured $browser/$os. session_id
  // is the correlation id shared with Sentry.
  posthog.register({
    platform: isElectron ? 'desktop' : 'web',
    // partner = the installed desktop build's partner; pbx_partner = the PBX's
    // partner key. They can differ (base app on a Dentrix PBX). 'default' = none.
    // Always real values, so unlike channel/app_version they never need clearing.
    partner: partner || 'default',
    pbx_partner: pbx.partner?.key || 'default',
    [OBSERVABILITY_SESSION_ID_KEY]: getObservabilitySessionId(),
  })

  // channel/app_version persist in localStorage, which the desktop popup and main
  // windows share. Register a resolved value or clear the key — never persist a
  // placeholder: one written once rides on every window's and later session's
  // events until something overwrites it (the source of "Unknown App Version" /
  // "Unknown Channel Name" rows in the version tiles). unregister() writes the
  // shared store, so this also purges a placeholder a pre-gate build left behind.
  registerOrClearSuperProperty('channel', channel)
  registerOrClearSuperProperty('app_version', appVersion)
  // channel is the saved update feed, which can differ from the installed build
  // after a switch; build_channel is the channel the running build shipped on.
  registerOrClearSuperProperty('build_channel', buildChannel)
  // Desktop build arch (sizes ia32 usage). Cleared on binaries that predate it.
  if (isElectron) {
    registerOrClearSuperProperty('arch', window.electron?.arch)
    // OS and version size the machines a minimumVersion would strand (Intel
    // Macs, macOS 12). Desktop events carry no $os: the app replaces the UA.
    registerOrClearSuperProperty('os_platform', window.electron?.osPlatform)
    registerOrClearSuperProperty('os_version', window.electron?.osVersion)
    registerArm64Translation()
    // The version counter app_version belongs to: each partner build numbers its
    // releases separately from the base app, so the same app_version can be two
    // different releases. Unlike partner, older builds never sent it. Cleared
    // when the partner lookup failed, so the dashboard falls back to its rules.
    registerOrClearSuperProperty('release_line', releaseLine)
  }

  const flagEnvironment = getApiEnvironment()
  if (flagEnvironment !== lastFlagEnvironment) {
    lastFlagEnvironment = flagEnvironment
    // Both flag-only. Guarded so this sends once per load, not a flag reload
    // every re-render.
    posthog.setPersonPropertiesForFlags({
      environment: flagEnvironment,
      release_ui_contract: RELEASE_UI_CONTRACT,
    })
  }

  const userId = user.id.toString()
  const personProperties = {
    name: user.first_name + ' ' + user.last_name,
    email: user.email,
    channel: channel ?? UNKNOWN_CHANNEL,
    app_version: appVersion ?? UNKNOWN_APP_VERSION,
    // Person properties (not a group) so flags can target per PBX.
    pbx_id: pbx.id,
    pbx_uuid: pbx.uuid,
    pbx_name: pbx.name,
    company_id: pbx.company_id,
    company_uuid: pbx.company_uuid,
    // Omitted when unknown so a missing value never overwrites a stored one.
    ...(pbx.company_country ? { company_country: pbx.company_country } : {}),
    partner: partner || 'default',
    pbx_partner: pbx.partner?.key || 'default',
  }

  // Skip identify unless the user or payload changed — the caller re-runs on every
  // render and each identify() is a $set write. The dedup record survives reloads;
  // the distinct_id check re-identifies if PostHog's own storage was cleared.
  const payloadKey = JSON.stringify(personProperties)
  const dedup = readIdentifyDedupState()
  const alreadyIdentified =
    dedup?.userId === userId &&
    dedup?.payloadKey === payloadKey &&
    posthog.get_distinct_id() === userId
  if (!alreadyIdentified) {
    posthog.identify(userId, personProperties)
    writeIdentifyDedupState({ userId, payloadKey })
  }

  schedulePreInitFlush()
}

/**
 * Resets PostHog and the identify dedup so identities don't bleed across users on
 * a shared device.
 */
export const resetPosthog = () => {
  if (!isPosthogReady()) {
    return
  }
  clearIdentifyDedupState()
  lastFlagEnvironment = undefined
  posthog.reset()
}

// Pre-init events (e.g. login signals — PostHog only initializes after login)
// are buffered, bounded, and flushed after init instead of dropped.
const MAX_PRE_INIT_EVENTS = 20
const preInitEvents: Array<{
  eventName: string
  data?: Record<string, unknown>
}> = []
let preInitFlushScheduled = false

/**
 * Flushes the buffer once feature flags have loaded — flushing earlier would
 * hit the fail-closed kill switch in before_send (flag not yet loaded reads
 * as disabled) and permanently drop the buffered events on fresh browsers.
 * Called after register()/identify() so events carry this app load's super
 * properties (notably the correlation session_id) rather than persisted ones
 * from a previous load.
 */
const schedulePreInitFlush = () => {
  if (preInitFlushScheduled || preInitEvents.length === 0) {
    return
  }
  preInitFlushScheduled = true
  posthog.onFeatureFlags(() => {
    preInitEvents
      .splice(0)
      .forEach(({ eventName, data }) => posthog.capture(eventName, data))
  })
}

// Flood guard: PostHog bills per event, and a stuck client (retry loop, stale
// desktop bundle) can emit the same event thousands of times an hour. Two
// layers stop that without truncating a power user doing bulk triage.
//
// Both layers live in memory per browser tab: a reload clears them and two tabs
// each get a full budget. sessionStorage would survive the reload but is still
// per tab, so it is not worth a write on every event.

// Layer 1 — a repeat of an identical payload carries no new information, so it
// is a loop; a fast user sends a different id each time.
const DUPLICATE_WINDOW_MS = 10 * 1000
// Components that mount several copies of the same hook fire an event more than
// once in the same tick. Only a repeat still arriving after this long is a loop.
const DUPLICATE_LOOP_AFTER_MS = 1000
// Bound the payload map; bulk triage keeps well under 100 keys in the window.
const DUPLICATE_MAX_KEYS = 500

// Layer 2 — backstop for a flood that varies its payload. The cap clears the
// fastest measured triage session (~800/hour) and stays far below loop rates.
const RATE_LIMIT_WINDOW_MS = 60 * 60 * 1000
const RATE_LIMIT_MAX_PER_NAME = 1000

const DAILY_WINDOW_MS = 24 * 60 * 60 * 1000
const DAILY_MAX_PER_NAME = 200

const RATE_LIMITED_MARKER_EVENT = 'shared_analytics_rate_limited'
// A flood is continuous, so the marker needs its own floor or it becomes one.
// A minute is short enough that a flood lasting a minute still reports a size.
const MARKER_MIN_INTERVAL_MS = 60 * 1000
// The floor has to scale with the window that fired. A client over the daily
// cap stays over it for the rest of the day, so a once-a-minute marker runs for
// ~22 hours and becomes the flood it reports: measured at 16,864 markers to
// report 25,189 drops. An hour still carries the full size in dropped_count,
// which accumulates into whichever marker sends next.
const DAILY_MARKER_INTERVAL_MS = 60 * 60 * 1000

type DropReason = 'duplicate_payload' | 'hourly_cap' | 'daily_cap'

interface RateRule {
  windowMs: number
  max: number
  reason: DropReason
  markerIntervalMs: number
}

const HOURLY_RULE: RateRule = {
  windowMs: RATE_LIMIT_WINDOW_MS,
  max: RATE_LIMIT_MAX_PER_NAME,
  reason: 'hourly_cap',
  markerIntervalMs: MARKER_MIN_INTERVAL_MS,
}

const DAILY_RULE: RateRule = {
  windowMs: DAILY_WINDOW_MS,
  max: DAILY_MAX_PER_NAME,
  reason: 'daily_cap',
  markerIntervalMs: DAILY_MARKER_INTERVAL_MS,
}

const DEFAULT_RATE_RULES: RateRule[] = [HOURLY_RULE]

// Transport health counters. Their payload is a bare close code, identical on
// every repeat, so a client stuck in connection churn stays under the hourly
// cap yet still bills thousands of rows a day that say nothing new. The daily
// rule subsumes the hourly one (a lower cap over a longer window), so it stands
// alone. Sum dropped_count on the shared_analytics_rate_limited marker to
// recover the true total.
const RATE_RULES_BY_NAME = new Map<string, RateRule[]>([
  ['ws_unexpected_closure', [DAILY_RULE]],
  ['ws_reconnect', [DAILY_RULE]],
  ['ws_connect_failed', [DAILY_RULE]],
  ['ws_silent_socket', [DAILY_RULE]],
  ['ws_page_resumed', [DAILY_RULE]],
  ['ws_clock_skew', [DAILY_RULE]],
  ['ws_rotation_skipped', [DAILY_RULE]],
])

const markerStates = new Map<
  string,
  { droppedCount: number; lastSentAtMs: number }
>()

/**
 * Emits the flood marker for one event name and reason, at most once per
 * intervalMs, carrying the drops since the previous marker — sum dropped_count
 * across markers for the size of a flood. Captured directly so the marker is
 * never subject to the guard it reports on.
 */
const reportDrop = (
  eventName: string,
  reason: DropReason,
  nowMs: number,
  intervalMs: number,
) => {
  const key = `${eventName}\u0000${reason}`
  let state = markerStates.get(key)
  if (!state) {
    // -Infinity, not 0, so the first marker sends whatever the clock reads.
    state = { droppedCount: 0, lastSentAtMs: Number.NEGATIVE_INFINITY }
    markerStates.set(key, state)
  }
  state.droppedCount++
  if (nowMs - state.lastSentAtMs < intervalMs) {
    return
  }
  state.lastSentAtMs = nowMs
  posthog.capture(RATE_LIMITED_MARKER_EVENT, {
    event_name: eventName,
    reason,
    dropped_count: state.droppedCount,
  })
  state.droppedCount = 0
}

const lastSentAtMsByPayload = new Map<string, number>()

const buildPayloadKey = (
  eventName: string,
  data?: Record<string, unknown>,
): string | null => {
  if (!data) {
    // Layer 1 reads an identical payload as "no new information, so it is a
    // loop". A payload-less event carries nothing to tell two occurrences
    // apart, so that reading is unsound: selecting one voicemail and then a
    // different one would collapse into a single key. Skip the layer and leave
    // a genuine loop to the hourly cap, which is the layer for floods that do
    // not vary their payload.
    return null
  }
  try {
    return `${eventName}\u0000${JSON.stringify(data)}`
  } catch {
    // Unserializable payload (cycle, BigInt): skip the layer, never drop.
    return null
  }
}

// Entries are always re-inserted on write, so iteration reaches the oldest first
// and can stop at the first one still inside the window.
const trimPayloadWindow = (nowMs: number) => {
  for (const [key, sentAtMs] of lastSentAtMsByPayload) {
    if (
      nowMs - sentAtMs < DUPLICATE_WINDOW_MS &&
      lastSentAtMsByPayload.size <= DUPLICATE_MAX_KEYS
    ) {
      return
    }
    lastSentAtMsByPayload.delete(key)
  }
}

const isDuplicate = (
  eventName: string,
  data: Record<string, unknown> | undefined,
  nowMs: number,
): boolean => {
  const key = buildPayloadKey(eventName, data)
  if (key === null) {
    return false
  }
  const sentAtMs = lastSentAtMsByPayload.get(key)
  if (sentAtMs !== undefined && nowMs - sentAtMs < DUPLICATE_WINDOW_MS) {
    if (nowMs - sentAtMs >= DUPLICATE_LOOP_AFTER_MS) {
      reportDrop(eventName, 'duplicate_payload', nowMs, MARKER_MIN_INTERVAL_MS)
    }
    return true
  }
  lastSentAtMsByPayload.delete(key)
  lastSentAtMsByPayload.set(key, nowMs)
  trimPayloadWindow(nowMs)
  return false
}

const sentAtMsByName = new Map<string, number[]>()

const isRateLimited = (eventName: string, nowMs: number): boolean => {
  const rules = RATE_RULES_BY_NAME.get(eventName) ?? DEFAULT_RATE_RULES
  let sentAtMs = sentAtMsByName.get(eventName)
  if (!sentAtMs) {
    sentAtMs = []
    sentAtMsByName.set(eventName, sentAtMs)
  }
  // Sliding window: a continuous flood stays capped instead of getting a fresh
  // budget — and a new marker — every hour. Trim to the longest rule's window;
  // shorter rules count a suffix of what survives.
  const longestWindowMs = Math.max(...rules.map((rule) => rule.windowMs))
  const oldestLiveIndex = sentAtMs.findIndex(
    (ms) => ms > nowMs - longestWindowMs,
  )
  if (oldestLiveIndex === -1) {
    sentAtMs.length = 0
  } else if (oldestLiveIndex > 0) {
    sentAtMs.splice(0, oldestLiveIndex)
  }
  // Timestamps ascend, so entries inside a window are the tail of the array.
  for (const rule of rules) {
    const firstLiveIndex = sentAtMs.findIndex(
      (ms) => ms > nowMs - rule.windowMs,
    )
    const liveCount =
      firstLiveIndex === -1 ? 0 : sentAtMs.length - firstLiveIndex
    if (liveCount >= rule.max) {
      reportDrop(eventName, rule.reason, nowMs, rule.markerIntervalMs)
      return true
    }
  }
  sentAtMs.push(nowMs)
  return false
}

/**
 * Forwards an analytics event to PostHog. The kill switch applies via the
 * before_send hook, like every other event. Never throws — callers include
 * API/auth flows and softphone callbacks that must not fail on telemetry.
 */
export const capturePosthogEvent = (
  eventName: string,
  data?: Record<string, unknown>,
) => {
  if (!isPosthogReady()) {
    if (preInitEvents.length < MAX_PRE_INIT_EVENTS) {
      preInitEvents.push({ eventName, data })
    }
    return
  }
  try {
    // A duplicate short-circuits before the hourly cap, so a loop cannot spend
    // the budget a real user needs for the same event name.
    const nowMs = Date.now()
    if (
      isDuplicate(eventName, data, nowMs) ||
      isRateLimited(eventName, nowMs)
    ) {
      return
    }
    posthog.capture(eventName, data)
  } catch {
    // Fire-and-forget; analytics errors must not affect the app.
  }
}
